import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useAppTheme } from '../contexts/ThemeContext';

const MetabaseDashboards = () => {
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [signedUrl, setSignedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentTheme } = useAppTheme();


  // Fetch available dashboards
  const { data: dashboards, isLoading: dashboardsLoading, error: dashboardsError } = useQuery({
    queryKey: ['metabaseDashboards'],
    queryFn: async () => {
      const response = await apiClient.get('/metabase/dashboards');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Get signed URL for the selected dashboard
  useEffect(() => {
    const getSignedUrl = async () => {
      if (!selectedDashboard) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/metabase/dashboard/${selectedDashboard}/signed-url`);
        const url = response.data.signedUrl;
        console.log('Received signed URL:', url);
        setSignedUrl(url);
      } catch (error) {
        console.error('Error fetching signed URL:', error);
        setError(error.response?.data?.detail || 'Failed to load dashboard URL');
      } finally {
        setLoading(false);
      }
    };

    getSignedUrl();
  }, [selectedDashboard]);

  const handleDashboardChange = (event) => {
    setSelectedDashboard(event.target.value);
  };



  // Function to validate if URL is likely a Metabase embed URL
  const isValidMetabaseUrl = (url) => {
    if (!url) return false;
    return url.includes('/embed/dashboard/') && url.includes('#');
  };

  if (dashboardsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dashboardsError) {
    return (
      <Typography color="error" sx={{ mt: 4 }}>
        Failed to load available dashboards.
      </Typography>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Analytics Header */}
      {currentTheme.isGradient ? (
        <Box className="page-header-gradient" sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Analytics Dashboards
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Monitor your clinic's performance and insights
          </Typography>
        </Box>
      ) : (
        <Typography variant="h4" gutterBottom>
          Analytics Dashboards
        </Typography>
      )}
      
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel id="dashboard-select-label">Select Dashboard</InputLabel>
          <Select
            labelId="dashboard-select-label"
            id="dashboard-select"
            value={selectedDashboard}
            label="Select Dashboard"
            onChange={handleDashboardChange}
          >
            {dashboards?.map((dashboard) => (
              <MenuItem key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>


      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {signedUrl && process.env.NODE_ENV !== 'production' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            <strong>Signed URL:</strong> {signedUrl}
          </Typography>
          <Typography variant="body2" mt={1}>
            <strong>URL Validation:</strong> {isValidMetabaseUrl(signedUrl) ? 
              '✅ Appears to be a valid Metabase URL' : 
              '❌ Does NOT appear to be a valid Metabase URL'}
          </Typography>
          {!isValidMetabaseUrl(signedUrl) && (
            <Typography variant="body2" color="error">
              The URL doesn't look like a Metabase embed URL. Make sure your METABASE_SITE_URL is set correctly.
            </Typography>
          )}
        </Alert>
      )}

      <Paper 
        elevation={3} 
        sx={{ 
          p: 0, 
          height: 'calc(100vh - 250px)', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : !selectedDashboard ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">Please select a dashboard to view</Typography>
          </Box>
        ) : !signedUrl ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">Failed to load dashboard URL</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3 }}>
            <Typography variant="h6" color="text.secondary">
              📊 Dashboard Ready to View
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '400px' }}>
              Click the button below to open your Metabase dashboard in a new tab for the best viewing experience.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => window.open(signedUrl, '_blank', 'noopener,noreferrer')}
              sx={{ mt: 2 }}
            >
              Open Dashboard in New Tab
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              🔒 Secure access • Token expires in 10 minutes
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MetabaseDashboards; 